import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/joy';
import { Trash as Trash2, Magnet, Ruler, Globe } from '@phosphor-icons/react';
import type { DrawingMode } from '../../types';
import { LINE_TYPE_CONFIG, type LineType, LINE_TYPES } from './lineDrawingConstants';

interface MapToolbarProps {
  mode: DrawingMode;
  activeLineType: LineType | null;
  onModeChange: (mode: DrawingMode) => void;
  onLineTypeChange: (type: LineType) => void;
  onDelete: () => void;
  hasSelection: boolean;
  readOnly: boolean;
  showLineDrawing?: boolean;
  snapEnabled?: boolean;
  onToggleSnap?: () => void;
  showDimensions?: boolean;
  onToggleDimensions?: () => void;
  onOpenGoogleEarth?: () => void;
}

// Mode button colors matching Proway-style toolbar
const MODE_STYLES = {
  view: { activeColor: '#4CAF50', label: 'View' },
  draw: { activeColor: '#4CAF50', label: 'Draw' },
  edit: { activeColor: '#4CAF50', label: 'Adjust' },
  draw_line: { activeColor: '#2196F3', label: '' }, // Not used as a single button
} as const;

export const MapToolbar: React.FC<MapToolbarProps> = ({
  mode,
  activeLineType,
  onModeChange,
  onLineTypeChange,
  onDelete,
  hasSelection,
  readOnly,
  showLineDrawing = true,
  snapEnabled = true,
  onToggleSnap,
  showDimensions = true,
  onToggleDimensions,
  onOpenGoogleEarth,
}) => {
  if (readOnly) return null;

  const isLineMode = mode === 'draw_line';

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        display: 'flex',
        gap: 0.5,
        p: 0.5,
        backgroundColor: 'rgba(20, 30, 50, 0.92)',
        borderRadius: 'md',
        boxShadow: 'lg',
        alignItems: 'center',
      }}
    >
      {/* Mode buttons */}
      <ToolbarChip
        label="View"
        isActive={mode === 'view'}
        activeColor="#4CAF50"
        onClick={() => onModeChange('view')}
      />
      <ToolbarChip
        label="Draw"
        isActive={mode === 'draw'}
        activeColor="#4CAF50"
        onClick={() => onModeChange('draw')}
      />
      <ToolbarChip
        label="Adjust"
        isActive={mode === 'edit'}
        activeColor="#4CAF50"
        onClick={() => onModeChange('edit')}
      />

      {/* Separator */}
      {showLineDrawing && (
        <Box
          sx={{
            width: '1px',
            height: 24,
            backgroundColor: 'rgba(255,255,255,0.2)',
            mx: 0.25,
          }}
        />
      )}

      {/* Line type buttons - always visible when line drawing enabled */}
      {showLineDrawing &&
        LINE_TYPES.map((type) => {
          const config = LINE_TYPE_CONFIG[type];
          const isActive = isLineMode && activeLineType === type;

          return (
            <ToolbarChip
              key={type}
              label={config.label}
              isActive={isActive}
              activeColor={config.color}
              dotColor={config.color}
              onClick={() => {
                if (mode !== 'draw_line') {
                  onModeChange('draw_line');
                }
                onLineTypeChange(type);
              }}
              shortcut={config.shortcut}
            />
          );
        })}

      {/* Toggle buttons separator */}
      {showLineDrawing && (onToggleSnap || onToggleDimensions || onOpenGoogleEarth) && (
        <Box
          sx={{
            width: '1px',
            height: 24,
            backgroundColor: 'rgba(255,255,255,0.2)',
            mx: 0.25,
          }}
        />
      )}

      {/* Snap toggle */}
      {showLineDrawing && onToggleSnap && (
        <Tooltip title={snapEnabled ? 'Snap ON (click to disable)' : 'Snap OFF (click to enable)'} size="sm">
          <IconButton
            variant="plain"
            size="sm"
            sx={{
              color: snapEnabled ? '#4CAF50' : 'rgba(255,255,255,0.4)',
              backgroundColor: snapEnabled ? 'rgba(76,175,80,0.2)' : 'transparent',
              '&:hover': { backgroundColor: snapEnabled ? 'rgba(76,175,80,0.3)' : 'rgba(255,255,255,0.1)' },
              minWidth: 28,
              minHeight: 28,
            }}
            onClick={onToggleSnap}
          >
            <Magnet size={14} />
          </IconButton>
        </Tooltip>
      )}

      {/* Dimensions toggle */}
      {showLineDrawing && onToggleDimensions && (
        <Tooltip title={showDimensions ? 'Hide Dimensions' : 'Show Dimensions'} size="sm">
          <IconButton
            variant="plain"
            size="sm"
            sx={{
              color: showDimensions ? '#2196F3' : 'rgba(255,255,255,0.4)',
              backgroundColor: showDimensions ? 'rgba(33,150,243,0.2)' : 'transparent',
              '&:hover': { backgroundColor: showDimensions ? 'rgba(33,150,243,0.3)' : 'rgba(255,255,255,0.1)' },
              minWidth: 28,
              minHeight: 28,
            }}
            onClick={onToggleDimensions}
          >
            <Ruler size={14} />
          </IconButton>
        </Tooltip>
      )}

      {/* Google Earth button */}
      {onOpenGoogleEarth && (
        <Tooltip title="Open in Google Earth (historical imagery)" size="sm">
          <IconButton
            variant="plain"
            size="sm"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
              minWidth: 28,
              minHeight: 28,
            }}
            onClick={onOpenGoogleEarth}
          >
            <Globe size={14} />
          </IconButton>
        </Tooltip>
      )}

      {/* Delete button */}
      {hasSelection && (
        <>
          <Box
            sx={{
              width: '1px',
              height: 24,
              backgroundColor: 'rgba(255,255,255,0.2)',
              mx: 0.25,
            }}
          />
          <Tooltip title="Delete Selected" size="sm">
            <IconButton
              variant="plain"
              size="sm"
              sx={{
                color: '#ff4444',
                '&:hover': { backgroundColor: 'rgba(255,68,68,0.2)' },
                minWidth: 28,
                minHeight: 28,
              }}
              onClick={onDelete}
            >
              <Trash2 size={14} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

// Reusable toolbar chip component
function ToolbarChip({
  label,
  isActive,
  activeColor,
  dotColor,
  onClick,
  shortcut,
}: {
  label: string;
  isActive: boolean;
  activeColor: string;
  dotColor?: string;
  onClick: () => void;
  shortcut?: string;
}) {
  return (
    <Tooltip title={shortcut ? `${label} (${shortcut})` : label} size="sm">
      <Box
        component="button"
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
          backgroundColor: isActive ? activeColor : 'transparent',
          fontWeight: isActive ? 'bold' : 'normal',
          fontSize: '11px',
          fontFamily: 'inherit',
          px: 1,
          py: 0,
          minHeight: 26,
          borderRadius: 'sm',
          border: 'none',
          outline: 'none',
          '&:hover': {
            backgroundColor: isActive ? activeColor : 'rgba(255,255,255,0.1)',
            color: '#fff',
          },
        }}
      >
        {dotColor && !isActive && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: dotColor,
              flexShrink: 0,
            }}
          />
        )}
        {label}
      </Box>
    </Tooltip>
  );
}
